"use client"

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react"
import { useUserSettings } from "@/context/user-settings-context"
import { getMap, setMap, MultiplierMap, isStorageAvailable } from "./price-multiplier-storage"

interface PriceMultiplierContextType {
  getMultiplier: (projectId: string, configuratorId: string) => number
  setMultiplier: (projectId: string, configuratorId: string, value: number) => void
  resetProject: (projectId: string) => void
  resetAll: () => void
  isLoaded: boolean
}

const PriceMultiplierContext = createContext<PriceMultiplierContextType | undefined>(undefined)

interface PriceMultiplierProviderProps {
  children: React.ReactNode
}

export function PriceMultiplierProvider({ children }: PriceMultiplierProviderProps) {
  const [map, setMapState] = useState<MultiplierMap>({})
  const [isLoaded, setIsLoaded] = useState(false)
  const { settings: userSettings, loading: userSettingsLoading } = useUserSettings()

  // Initialize the multiplier map from localStorage
  useEffect(() => {
    if (isStorageAvailable()) {
      try {
        const storedMap = getMap()
        setMapState(storedMap)
      } catch (error) {
        console.error('Failed to load price multiplier map:', error)
        setMapState({})
      }
    }
    setIsLoaded(true)
  }, [])

  // Persist changes to localStorage
  useEffect(() => {
    if (isLoaded && isStorageAvailable()) {
      try {
        setMap(map)
      } catch (error) {
        console.error('Failed to save price multiplier map:', error)
      }
    }
  }, [map, isLoaded])

  // Get multiplier with precedence rules
  const getMultiplier = useCallback((projectId: string, configuratorId: string): number => {
    // 1. Check project-specific override (localStorage)
    const projectOverride = map[projectId]?.[configuratorId]
    if (typeof projectOverride === 'number' && projectOverride > 0) {
      return projectOverride
    }

    // 2. Check user settings default multiplier (from backend)
    if (!userSettingsLoading && userSettings) {
      const userSetting = userSettings.find(setting => setting.configurator_type === configuratorId)
      if (userSetting && typeof userSetting.price_multiplier === 'number' && userSetting.price_multiplier > 0) {
        return userSetting.price_multiplier
      }
    }

    // 3. Fallback to 1 (no change)
    return 1
  }, [map, userSettings, userSettingsLoading])

  // Set multiplier for a specific project and configurator
  const setMultiplier = useCallback((projectId: string, configuratorId: string, value: number) => {
    if (!projectId || !configuratorId || typeof value !== 'number' || value < 1 || value > 10) {
      console.warn('Invalid parameters for setMultiplier:', { projectId, configuratorId, value })
      return
    }

    setMapState(prevMap => {
      const newMap = { ...prevMap }
      
      // Initialize project if it doesn't exist
      if (!newMap[projectId]) {
        newMap[projectId] = {}
      }
      
      // Set the multiplier value
      newMap[projectId][configuratorId] = value
      
      return newMap
    })
  }, [])

  // Reset all multipliers for a specific project
  const resetProject = useCallback((projectId: string) => {
    if (!projectId) {
      console.warn('Invalid projectId for resetProject:', projectId)
      return
    }

    setMapState(prevMap => {
      const newMap = { ...prevMap }
      delete newMap[projectId]
      return newMap
    })
  }, [])

  // Reset all multipliers
  const resetAll = useCallback(() => {
    setMapState({})
  }, [])

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    getMultiplier,
    setMultiplier,
    resetProject,
    resetAll,
    isLoaded
  }), [getMultiplier, setMultiplier, resetProject, resetAll, isLoaded])

  return (
    <PriceMultiplierContext.Provider value={contextValue}>
      {children}
    </PriceMultiplierContext.Provider>
  )
}

export function usePriceMultiplier(): PriceMultiplierContextType {
  const context = useContext(PriceMultiplierContext)
  if (context === undefined) {
    throw new Error('usePriceMultiplier must be used within a PriceMultiplierProvider')
  }
  return context
}

/**
 * Hook to get a multiplier for a specific project and configurator
 * This is a convenience hook that directly returns the multiplier value
 */
export function useProjectMultiplier(projectId: string, configuratorId: string): number {
  const { getMultiplier } = usePriceMultiplier()
  return useMemo(() => {
    if (!projectId || !configuratorId) return 1
    return getMultiplier(projectId, configuratorId)
  }, [getMultiplier, projectId, configuratorId])
}

/**
 * Hook to check if a project has any custom multipliers set
 */
export function useProjectHasCustomMultipliers(projectId: string): boolean {
  const { getMultiplier } = usePriceMultiplier()
  const { settings: userSettings, loading: userSettingsLoading } = useUserSettings()
  
  return useMemo(() => {
    if (!projectId || userSettingsLoading) return false
    
    // Get the stored map to check for project-specific overrides
    const storedMap = isStorageAvailable() ? getMap() : {}
    const projectOverrides = storedMap[projectId]
    
    if (!projectOverrides || Object.keys(projectOverrides).length === 0) {
      return false
    }
    
    // Check if any of the project's multipliers differ from user defaults
    return Object.entries(projectOverrides).some(([configuratorId, multiplier]) => {
      const userDefault = userSettings?.find(setting => setting.configurator_type === configuratorId)?.price_multiplier || 1
      return Math.abs(multiplier - userDefault) > 0.001 // Allow for floating point precision
    })
  }, [projectId, userSettings, userSettingsLoading, getMultiplier])
} 